import { errorHelpers, oauthHelpers } from '@approvalmax/utils';
import { domain } from 'modules/data';
import { useCallback } from 'react';
import { routingService } from 'services/routing';
import {
    useGetGoogleOAuthUrl,
    useGetMicrosoftOAuthUrl,
    useGetQBooksOAuthUrl,
    useGetXeroOAuthUrl,
} from 'shared/data/webApp/v1';
import { getUrl, Path } from 'urlBuilder';

import { SignInWithOptions } from './useSignInWith.types';

export const useSignInWith = (options: SignInWithOptions) => {
    const { startingPoint, app, postAction, afterLoginRedirectUrl, tenantId } = options;

    const redirectUrl = getUrl(Path.oauth2Redirect);
    const uiState = encodeURIComponent(
        JSON.stringify({
            app,
            postAction,
            afterLoginRedirectUrl,
            startingPoint,
            tenantId,
        })
    );

    const ssoRequestParams = {
        redirectUrl,
        uiState,
    };

    const { refetch: refetchXeroOAuthUrl, isFetching: isFetchingXeroOAuth } = useGetXeroOAuthUrl(
        {
            query: {
                ...ssoRequestParams,
                ...{
                    ssoStartingPoint:
                        startingPoint === domain.SsoStartingPoint.ApprovalMax ? 'approvalmax' : 'xero-marketplace',
                },
            },
        },
        { enabled: false }
    );

    const { refetch: refetchQBooksOAuthUrl, isFetching: isFetchingQBooksOAuth } = useGetQBooksOAuthUrl(
        {
            query: {
                ...ssoRequestParams,
                ...{
                    ssoStartingPoint:
                        startingPoint === domain.SsoStartingPoint.ApprovalMax ? 'approvalmax' : 'qbooks-marketplace',
                },
            },
        },
        { enabled: false }
    );

    const { refetch: refetchGoogleOAuthUrl, isFetching: isFetchingGoogleOAuth } = useGetGoogleOAuthUrl(
        {
            query: {
                ...ssoRequestParams,
                ...{
                    ssoStartingPoint: 'approvalmax',
                },
            },
        },
        { enabled: false }
    );

    const { refetch: refetchMicrosoftOAuthUrl, isFetching: isFetchingMicrosoftOAuth } = useGetMicrosoftOAuthUrl(
        {
            query: {
                ...ssoRequestParams,
                ...{
                    ssoStartingPoint: 'approvalmax',
                },
            },
        },
        { enabled: false }
    );

    const progress = isFetchingXeroOAuth || isFetchingQBooksOAuth || isFetchingGoogleOAuth || isFetchingMicrosoftOAuth;

    const onSignInWith = useCallback(
        async (oauthProvider: domain.OAuthProvider) => {
            let oauthRedirectUrl: string | undefined = redirectUrl;

            try {
                switch (oauthProvider) {
                    case domain.OAuthProvider.Xero: {
                        const response = await refetchXeroOAuthUrl();

                        oauthRedirectUrl = response.data?.data.xeroOAuthUrl;
                        break;
                    }
                    case domain.OAuthProvider.QBooks: {
                        const response = await refetchQBooksOAuthUrl();

                        oauthRedirectUrl = response.data?.data.qBooksOAuthUrl;
                        break;
                    }
                    case domain.OAuthProvider.Google: {
                        const response = await refetchGoogleOAuthUrl();

                        oauthRedirectUrl = response.data?.data.googleOAuthUrl;
                        break;
                    }
                    case domain.OAuthProvider.Microsoft: {
                        const response = await refetchMicrosoftOAuthUrl();

                        oauthRedirectUrl = response.data?.data.microsoftOAuthUrl;
                        break;
                    }

                    default:
                        throw errorHelpers.assertNever(oauthProvider);
                }

                if (!oauthRedirectUrl) {
                    return;
                }

                oauthRedirectUrl = oauthHelpers.encodeOAuthState(oauthRedirectUrl, {
                    type: 'sso',
                    provider: oauthProvider,
                    startingPoint,
                });

                routingService.navigateToExternalUrl(oauthRedirectUrl);
            } catch (error) {
                errorHelpers.captureException(error);
            }
        },
        [
            redirectUrl,
            startingPoint,
            refetchXeroOAuthUrl,
            refetchQBooksOAuthUrl,
            refetchGoogleOAuthUrl,
            refetchMicrosoftOAuthUrl,
        ]
    );

    return {
        progress,
        onSignInWith,
    };
};
